import { useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { useUpdateBooking } from "../../../../../api/fleet-management/bookings/booking.hooks";
import { formatBookingAmount } from "../utils/booking.utils";

const initialMileageForm = {
  beforeMeterReading: "",
  afterMeterReading: "",
  totalMileage: "",
};

const getVehicleIdValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value?._id || value?.id || "";
};

const formatMileageValue = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const mapVehicleToMileageForm = (vehicle) => ({
  beforeMeterReading:
    vehicle?.beforeMeterReading !== undefined && vehicle?.beforeMeterReading !== null
      ? String(vehicle.beforeMeterReading)
      : "",
  afterMeterReading:
    vehicle?.afterMeterReading !== undefined && vehicle?.afterMeterReading !== null
      ? String(vehicle.afterMeterReading)
      : "",
  totalMileage:
    vehicle?.totalMileage !== undefined && vehicle?.totalMileage !== null
      ? String(vehicle.totalMileage)
      : "",
});

const BookingVehicleInformationSection = ({ bookingId, vehicles = [] }) => {
  const [showMileageModal, setShowMileageModal] = useState(false);
  const [selectedVehicleIndex, setSelectedVehicleIndex] = useState(-1);
  const [selectedVehicleHasMileage, setSelectedVehicleHasMileage] = useState(false);
  const [form, setForm] = useState(initialMileageForm);
  const [errors, setErrors] = useState({});
  const [feedback, setFeedback] = useState({ type: "", message: "" });

  const { mutate: updateBooking, isLoading: isUpdating } = useUpdateBooking();

  const vehicleRows = useMemo(() => {
    return vehicles.map((vehicle, index) => {
      const vehicleId = getVehicleIdValue(vehicle?.vehicleId);
      const vehicleLabel =
        vehicle?.snapshot?.name ||
        vehicle?.vehicleId?.name ||
        [vehicle?.snapshot?.make, vehicle?.snapshot?.model].filter(Boolean).join(" ") ||
        [vehicle?.vehicleId?.make, vehicle?.vehicleId?.model].filter(Boolean).join(" ") ||
        "Vehicle";

      return {
        index,
        vehicleId,
        vehicleLabel,
        fuelType: vehicle?.snapshot?.fuelType || vehicle?.vehicleId?.fuelType || "-",
        seatingCapacity:
          vehicle?.snapshot?.specifications?.seatingCapacity ||
          vehicle?.vehicleId?.specifications?.seatingCapacity ||
          "-",
        price: vehicle?.price ?? vehicle?.pricePerUnit ?? vehicle?.totalAmount,
        beforeMeterReading: vehicle?.beforeMeterReading,
        afterMeterReading: vehicle?.afterMeterReading,
        totalMileage: vehicle?.totalMileage,
      };
    });
  }, [vehicles]);

  const openMileageModal = (vehicleRow) => {
    if (!vehicleRow?.vehicleId) return;

    const originalVehicle = vehicles[vehicleRow.index];

    setFeedback({ type: "", message: "" });
    setErrors({});
    setSelectedVehicleIndex(vehicleRow.index);
    setSelectedVehicleHasMileage(
      vehicleRow.beforeMeterReading !== undefined ||
        vehicleRow.afterMeterReading !== undefined ||
        vehicleRow.totalMileage !== undefined
    );
    setForm(mapVehicleToMileageForm(originalVehicle));
    setShowMileageModal(true);
  };

  const closeMileageModal = () => {
    if (isUpdating) return;
    setShowMileageModal(false);
    setSelectedVehicleIndex(-1);
    setSelectedVehicleHasMileage(false);
  };

  const updateField = (key, value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };

      const before = Number(key === "beforeMeterReading" ? value : prev.beforeMeterReading);
      const after = Number(key === "afterMeterReading" ? value : prev.afterMeterReading);

      if (Number.isFinite(before) && Number.isFinite(after) && after >= before) {
        next.totalMileage = String(after - before);
      }

      return next;
    });

    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validateMileageForm = () => {
    const nextErrors = {};
    const before = Number(form.beforeMeterReading);
    const after = Number(form.afterMeterReading);
    const total = Number(form.totalMileage);

    if (!Number.isFinite(before) || before < 0) {
      nextErrors.beforeMeterReading = "Before meter reading is required.";
    }

    if (!Number.isFinite(after) || after < 0) {
      nextErrors.afterMeterReading = "After meter reading is required.";
    }

    if (Number.isFinite(before) && Number.isFinite(after) && after < before) {
      nextErrors.afterMeterReading = "After reading must be greater than or equal to before reading.";
    }

    if (!Number.isFinite(total) || total < 0) {
      nextErrors.totalMileage = "Total mileage is required.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmitMileage = (e) => {
    e.preventDefault();
    setFeedback({ type: "", message: "" });

    if (!bookingId || selectedVehicleIndex < 0 || !validateMileageForm()) return;

    const nextVehicles = vehicles.map((vehicle, index) => {
      if (index !== selectedVehicleIndex) return vehicle;

      return {
        ...vehicle,
        beforeMeterReading: Number(form.beforeMeterReading),
        afterMeterReading: Number(form.afterMeterReading),
        totalMileage: Number(form.totalMileage),
      };
    });

    updateBooking(
      { bookingId, bookingData: { vehicles: nextVehicles } },
      {
        onSuccess: () => {
          setFeedback({ type: "success", message: "Mileage details updated successfully." });
          closeMileageModal();
        },
        onError: (updateError) => {
          setFeedback({
            type: "danger",
            message: updateError?.response?.data?.message || "Failed to update mileage details.",
          });
        },
      }
    );
  };

  if (!bookingId) return null;

  return (
    <div className="card h-100">
      <div className="card-header border-0 pb-0">
        <h5 className="mb-0">Vehicle Information</h5>
      </div>
      <div className="card-body pt-3">
        {feedback.message ? (
          <Alert variant={feedback.type} className="mb-3">
            {feedback.message}
          </Alert>
        ) : null}

        {vehicleRows.length === 0 ? (
          <div className="text-muted">No vehicle data available.</div>
        ) : (
          <div className="table-responsive">
            <Table bordered hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Vehicle</th>
                  <th>Fuel Type</th>
                  <th>Seating Capacity</th>
                  <th>Before Reading</th>
                  <th>After Reading</th>
                  <th>Total Mileage</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vehicleRows.map((vehicleRow, index) => {
                  const hasMileage =
                    vehicleRow.beforeMeterReading !== undefined ||
                    vehicleRow.afterMeterReading !== undefined ||
                    vehicleRow.totalMileage !== undefined;

                  return (
                    <tr key={vehicleRow.vehicleId || `${vehicleRow.vehicleLabel}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{vehicleRow.vehicleLabel}</td>
                      <td>{formatMileageValue(vehicleRow.fuelType)}</td>
                      <td>{formatMileageValue(vehicleRow.seatingCapacity)}</td>
                      <td>{formatMileageValue(vehicleRow.beforeMeterReading)}</td>
                      <td>{formatMileageValue(vehicleRow.afterMeterReading)}</td>
                      <td>{formatMileageValue(vehicleRow.totalMileage)}</td>
                      <td>
                        <Button
                          variant={hasMileage ? "outline-primary" : "primary"}
                          size="sm"
                          onClick={() => openMileageModal(vehicleRow)}
                          disabled={isUpdating}
                        >
                          {hasMileage ? "Edit Mileage Details" : "Add Mileage Details"}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}
      </div>

      <Modal show={showMileageModal} onHide={closeMileageModal} centered>
        <Modal.Header closeButton={!isUpdating}>
          <Modal.Title>
            {selectedVehicleHasMileage ? "Edit Mileage Details" : "Add Mileage Details"}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitMileage}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Before Meter Reading</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.beforeMeterReading}
                    onChange={(e) => updateField("beforeMeterReading", e.target.value)}
                    disabled={isUpdating}
                    isInvalid={!!errors.beforeMeterReading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.beforeMeterReading}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>After Meter Reading</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.afterMeterReading}
                    onChange={(e) => updateField("afterMeterReading", e.target.value)}
                    disabled={isUpdating}
                    isInvalid={!!errors.afterMeterReading}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.afterMeterReading}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label>Total Mileage</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    step="0.1"
                    value={form.totalMileage}
                    onChange={(e) => updateField("totalMileage", e.target.value)}
                    disabled={isUpdating}
                    isInvalid={!!errors.totalMileage}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.totalMileage}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="light" onClick={closeMileageModal} disabled={isUpdating}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                selectedVehicleHasMileage ? "Update Mileage Details" : "Save Mileage Details"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default BookingVehicleInformationSection;